// What one member of staff may do to another's account.
//
// Every admin can do everything in the backoffice except manage each other.
// The API enforces this (see UserHandler); this only decides which buttons
// are worth showing.

export interface StaffAccount {
  id: number;
  role: string;
  status: string;
}

export interface StaffActions {
  changePassword: boolean;
  /** Generate a reset link to hand over, for someone who forgot theirs. */
  resetLink: boolean;
  edit: boolean;
  activate: boolean;
  revoke: boolean;
  remove: boolean;
}

const NONE: StaffActions = {
  changePassword: false,
  resetLink: false,
  edit: false,
  activate: false,
  revoke: false,
  remove: false,
};

export function isSuperAdmin(account: Pick<StaffAccount, 'role'> | null | undefined): boolean {
  return account?.role === 'super_admin';
}

/**
 * The actions `me` may take on `target`.
 *
 *  - The super admin manages every account. Their own cannot be revoked or
 *    removed, by anyone: there is one, and nobody else can manage staff.
 *    A reset link is for someone else's active account: whoever is signed
 *    in changes their own password directly.
 *  - An admin manages nobody but themselves, and of their own account only
 *    the password from this screen (name and email live on the profile page).
 */
export function staffActions(
  me: Pick<StaffAccount, 'id' | 'role'> | null | undefined,
  target: StaffAccount,
): StaffActions {
  if (!me) return NONE;

  const own = me.id === target.id;

  if (!isSuperAdmin(me)) {
    return own ? { ...NONE, changePassword: true } : NONE;
  }

  const targetIsSuper = isSuperAdmin(target);
  return {
    changePassword: true,
    resetLink: !own && target.status === 'active',
    edit: true,
    activate: !targetIsSuper && (target.status === 'pending' || target.status === 'inactive'),
    revoke: !targetIsSuper && target.status === 'active',
    remove: !targetIsSuper,
  };
}
